import { FC, useEffect, useEffectEvent } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Renew } from '@mint-lib/icons';
import { enqueueSnackbar, toast } from '@mint-lib/notifications';
import { useQueryClient } from '@mint-lib/query-client';
import { Button } from '@mint-lib/tw-components';
import { ApprovalFlows } from '@mint-sdk/api-approval-flows';

import useFlowProcessDetailsData from '../api/queries/useFlowProcessDetailsData.js';
import useGetFlowStatusData from '../api/queries/useGetFlowStatusData.js';
import { useSetApprovalProcessData } from '../context/approvalProcess/hooks.jsx';
import useApprovalSocketConnection from '../hooks/useApprovalSocketConnection.js';
import { ApprovalStatusBarProps } from '../types/shared.js';
import {
  resolveUserStatusFromProcessStatusData,
  resolveProcessStatusFromProcessStatusData,
} from '../utils/shared.js';
import StatusBarRenderer from './StatusBarRenderer.jsx';
import StatusBarSkeleton from './StatusBarSkeleton.jsx';

const StatusBarContainer: FC<ApprovalStatusBarProps> = ({
  entityType,
  entityId,
  entityName,
  entitySource,
  entityMetadata,
}) => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit_v2');
  const queryClient = useQueryClient();

  useApprovalSocketConnection([{ entityId, entityType, entitySource }]);

  const {
    data: flowStatusData,
    isPending: isFlowStatusPending,
    isError: isFlowStatusError,
  } = useGetFlowStatusData({
    entityId,
    entityType,
    entitySource,
  });

  const isProcessDetailsEnabled =
    !!flowStatusData?.requestId &&
    flowStatusData.userStatus === 'approver' &&
    flowStatusData.requestStatus === 'pending';

  const { isPending: isProcessDetailsPending, isError: isProcessDetailsError } =
    useFlowProcessDetailsData({
      enabled: isProcessDetailsEnabled,
      approvalProcessId: flowStatusData?.requestId ?? '',
    });

  const isPending =
    isFlowStatusPending || (isProcessDetailsEnabled && isProcessDetailsPending);
  const isError =
    isFlowStatusError || (isProcessDetailsEnabled && isProcessDetailsError);
  const approvalProcessId = flowStatusData?.requestId ?? null;
  const userStatus = resolveUserStatusFromProcessStatusData(flowStatusData);
  const approvalProcessStatus =
    resolveProcessStatusFromProcessStatusData(flowStatusData);

  const setApprovalProcessData = useSetApprovalProcessData();

  const showErrorNotification = useEffectEvent(() => {
    const id = crypto.randomUUID();

    const handleRetry = () => {
      if (isProcessDetailsError && flowStatusData?.requestId) {
        ApprovalFlows.getRequest.invalidate(queryClient, {
          urlParams: {
            requestId: flowStatusData.requestId,
          },
        });
      }

      if (isFlowStatusError) {
        ApprovalFlows.checkFlowStatus.invalidate(queryClient, {
          params: {
            entityId,
            entityType,
            entitySource,
          },
        });
      }

      toast.dismiss(id);
    };

    enqueueSnackbar({
      variant: 'error',
      title: t('Could not load approval data.'),
      actions: (
        <Button
          className="pointer-events-auto"
          variant="outlined"
          color="error"
          size="small"
          onClick={handleRetry}
        >
          <Renew className="size-4" />
          {t('Try again')}
        </Button>
      ),
      removable: false,
      id,
    });
  });

  useEffect(() => {
    if (isFlowStatusPending) {
      setApprovalProcessData({ isPending: true, isError: false, data: null });
      return;
    }

    if (isFlowStatusError) {
      setApprovalProcessData({
        isPending: false,
        isError: true,
        data: null,
      });
      return;
    }

    setApprovalProcessData({
      isPending: false,
      isError: false,
      data: {
        userStatus,
        approvalProcessId,
        approvalProcessStatus,
      },
    });
  }, [
    isFlowStatusError,
    isFlowStatusPending,
    setApprovalProcessData,
    userStatus,
    approvalProcessStatus,
    approvalProcessId,
  ]);

  useEffect(() => {
    if (isError) {
      showErrorNotification();
    }
  }, [isError]);

  if (isPending || isError || !flowStatusData) {
    return (
      <StatusBarSkeleton
        aria-label={
          isFlowStatusError
            ? 'Approval status bar loading failed'
            : 'Approval status bar is loading'
        }
      />
    );
  }

  return (
    <StatusBarRenderer
      userStatus={userStatus}
      approvalProcessStatus={approvalProcessStatus}
      approvalProcessId={approvalProcessId}
      entityType={entityType}
      entityId={entityId}
      entityName={entityName}
      entitySource={entitySource}
      entityMetadata={entityMetadata}
    />
  );
};

export default StatusBarContainer;
